import React, { createContext, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { expVal } from '@confluence/feature-experiments';

import { LoadingIndicator } from '../progressive/LoadingIndicator.tsx';

import { getBatchIndex } from './getBatchIndex.ts';
import type { LightSubtreeScheduler } from './LightSubtreeScheduler.ts';

export const LightRootRenderer: React.MemoExoticComponent<
	(props: LightRootRendererProps) => React.JSX.Element
> = memo((props: LightRootRendererProps): React.JSX.Element => {
	const { batchEndIndices, children, onBatchRendered, scheduler } = props;
	const { createEvent } = useConfluenceAnalyticsEvents();
	const createEventRef = useRef(createEvent);

	useEffect(() => {
		createEventRef.current = createEvent;
	});

	useEffect(
		() =>
			scheduler?.start(
				expVal('cc_light_mode_scheduler_wait_metrics', 'isEnabled', false)
					? (attributes) => {
							createEventRef
								.current({
									type: 'sendOperationalEvent',
									data: {
										action: 'measured',
										actionSubject: 'lightSubtreeWait',
										source: 'lightRootRenderer',
										attributes,
									},
								})
								.fire();
						}
					: undefined,
			),
		[scheduler],
	);

	const seq = useBatchSequence(props);
	const [seqIndex, setSeqIndex] = useState(0);

	const lastSeqIndex = batchEndIndices.length - 1;

	useEffect(() => {
		// eslint-disable-next-line compat/compat
		const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
		const cancelHandle = requestIdleCallback(() => {
			const done = seqIndex >= lastSeqIndex;

			onBatchRendered?.(done);

			if (!done) {
				setSeqIndex((pendingState) =>
					pendingState < lastSeqIndex ? pendingState + 1 : pendingState,
				);
			}
		});

		return () => {
			const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
			cancelIdleCallback(cancelHandle);
		};
	}, [seqIndex, lastSeqIndex, onBatchRendered, setSeqIndex]);

	const maxBatchRenderIndex =
		batchEndIndices[seqIndex] ?? batchEndIndices[batchEndIndices.length - 1];
	const shouldRenderNode = useCallback(
		(renderIndex: number) => {
			if (seq) {
				// Alternating sequence:
				const batchIndex = getBatchIndex(renderIndex, batchEndIndices);
				if (batchIndex < seq.length) {
					return seq[batchIndex] <= seqIndex;
				}
			} else {
				// Linear sequence:
				return !maxBatchRenderIndex || renderIndex < maxBatchRenderIndex;
			}
		},
		[batchEndIndices, maxBatchRenderIndex, seq, seqIndex],
	);

	// The first version of progressive rendering of ADF that went into production used the linear sequence and did not have a loading indicator.
	// But the alternating sequence renders from a linked anchor that's not in the first batch and is not a generally well-known user experience on the Web.
	// That's why we'll start with a loading indicator at the top in the case of the alternating sequence for now.
	const loadingIndicator =
		seq &&
		seq.length > 0 &&
		// The node at `renderIndex` 0 is the `doc`, which is never visualized, so the loading indicator is not necessary as soon as the node at `renderIndex` 1 is rendered:
		!shouldRenderNode(1) ? (
			<LoadingIndicator />
		) : null;

	return (
		<>
			{loadingIndicator}
			<LightNodeRendererContext.Provider value={shouldRenderNode}>
				{children}
			</LightNodeRendererContext.Provider>
		</>
	);
});
LightRootRenderer.displayName = 'LightRootRenderer';

type LightRootRendererProps = PropsWithChildren<{
	batchEndIndices: readonly number[];
	onBatchRendered?: (done: boolean) => void;
	targetBatchIndex?: number;
	scheduler?: LightSubtreeScheduler;
}>;

type LightNodeRendererContextType = ((renderIndex: number) => boolean | undefined) | undefined;
export const LightNodeRendererContext: React.Context<LightNodeRendererContextType> =
	createContext<LightNodeRendererContextType>(undefined);

function useBatchSequence({
	batchEndIndices,
	targetBatchIndex = 0,
}: Pick<LightRootRendererProps, 'batchEndIndices' | 'targetBatchIndex'>) {
	type BatchSequence = {
		// What resets/re-initializes the sequence:
		batchEndIndices: readonly number[];

		// The sequence:
		batches: Array<number> | undefined;
	};

	const ref = useRef<BatchSequence | undefined>(undefined);

	if (ref.current?.batchEndIndices !== batchEndIndices) {
		let batches = ref.current?.batches;

		if (targetBatchIndex) {
			// Alternating sequence:
			const batchCount = batchEndIndices.length;

			if (batches?.length !== batchCount) {
				// Generate the sequence of batches to render, starting from `targetBatchIndex`:
				batches = new Array(batchCount);

				let seq = 0;

				batches[targetBatchIndex] = seq++;

				let above = targetBatchIndex - 1;
				let below = targetBatchIndex + 1;
				let yieldAbove = false;

				while (above >= 0 || below < batchCount) {
					if (yieldAbove) {
						if (above >= 0) {
							batches[above] = seq++;
							above--;
						}
					} else if (below < batchCount) {
						batches[below] = seq++;
						below++;
					}

					yieldAbove = !yieldAbove;
				}
			}
		} else {
			// Linear sequence:
			batches = undefined;
		}

		ref.current = {
			batchEndIndices,

			batches,
		};
	}

	return ref.current.batches;
}
